import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Edit, Copy, Archive, RotateCcw, Settings, Phone, Mic, Clock, DollarSign, ArrowLeft, User, Search } from 'lucide-react';
import { navigateWithCurrentQuery } from '../../../utils/routerUtils.js';
import ModernTimeDropdown from '../../../dashboard-components/layouts/modernDropdown.jsx';
import ModernMultiSelect from '../../../dashboard-components/layouts/ModernMultiSelect.jsx';
import server from '../../../api/server.js';
import { FaTimes } from 'react-icons/fa';
import ExploreVoicesModal from '../../layouts/ExploreVoicesModal.jsx';
import { useToast } from '../../../context/ToastContext.jsx';
import { isLiveKitBrowserCall, startLiveKitBrowserCall } from './browserCallLiveKit.js';

const AgentsPage = ({ clientID, selectedOptions }) => {
  const { showToast } = useToast();
  const [view, setView] = useState('list'); // 'list' or 'configure'
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agents, setAgents] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [client_id, setClient_id] = useState(null);
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // State for the agent creation modal
  const [showCreateAgentModal, setShowCreateAgentModal] = useState(false);
  const [newAgentName, setNewAgentName] = useState('');
  const [newAgentError, setNewAgentError] = useState('');
  const [showArchivedAgents, setShowArchivedAgents] = useState(false);
  const [agentSearchQuery, setAgentSearchQuery] = useState('');
  const [agentActionModal, setAgentActionModal] = useState({
    open: false,
    action: '',
    agent: null,
  });
  const [agentActionLoading, setAgentActionLoading] = useState(false);
  
  // WebRTC state variables
  const [isCallActive, setIsCallActive] = useState(false);
  const [callStatus, setCallStatus] = useState('');
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioQuality, setAudioQuality] = useState('Unknown');
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const livekitClientRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const audioElementRef = useRef(null);
  // Add these new state variables
const [isRecording, setIsRecording] = useState(false);
const [recordingDuration, setRecordingDuration] = useState(0);
const [recordingInfo, setRecordingInfo] = useState(null);
const [recordings, setRecordings] = useState([]);

// Add this ref for recording interval
const recordingIntervalRef = useRef(null);
const [showVoicesModal, setShowVoicesModal] = useState(false);
  const [testWorkflowOption, setTestWorkflowOption] = useState('');
  const [showOutboundModal, setShowOutboundModal] = useState(false);
  const [outboundPhoneNumber, setOutboundPhoneNumber] = useState('');
  // Store the entire config object from API
  const [configData, setConfigData] = useState({
    voice: {},
    llm: {},
    transcription: {},
    background_effects: {}
  });
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  
  // For dropdown options
  const [llmProviderOptions, setLlmProviderOptions] = useState([]);
  const [llmModelOptions, setLlmModelOptions] = useState([]);
  const [voiceProviderOptions, setVoiceProviderOptions] = useState([]);
  const [voiceModelOptions, setVoiceModelOptions] = useState([]);
  const [voiceOptions, setVoiceOptions] = useState([]);
  const [voiceGenderOptions, setVoiceGenderOptions] = useState(['female', 'male']);
  const [selectedVoiceGender, setSelectedVoiceGender] = useState('female');
  const [transcriptionProviderOptions, setTranscriptionProviderOptions] = useState([]);
  const [transcriptionModelOptions, setTranscriptionModelOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [secondaryLanguageOptions, setSecondaryLanguageOptions] = useState([]);

  // Migrate flat fields to nested conversation config and ensure defaults
  useEffect(() => {
    if (!selectedAgent) return;
    const current = selectedAgent;
    let changed = false;
    const updated = { ...current };

    // Conversation Hangup
    if (!updated.conversation_hangup) {
      updated.conversation_hangup = {
        hangup_based_on_intent_instruction: '',
        hangup_message: ''
      };
      changed = true;
    } else {
      if (updated.conversation_hangup.hangup_based_on_intent_instruction == null) {
        updated.conversation_hangup.hangup_based_on_intent_instruction = '';
        changed = true;
      }
      if (updated.conversation_hangup.hangup_message == null) {
        updated.conversation_hangup.hangup_message = '';
        changed = true;
      }
      if (updated.conversation_hangup.hangup_after_silence_seconds !== undefined) {
        delete updated.conversation_hangup.hangup_after_silence_seconds;
        changed = true;
      }
    }

    // Conversation Termination
    if (!updated.conversation_termination) {
      updated.conversation_termination = {
        maximum_conversation_duration: current?.max_conversation_time ?? '',
        termination_message: ''
      };
      changed = true;
    } else {
      if (
        updated.conversation_termination.maximum_conversation_duration == null &&
        current?.max_conversation_time != null
      ) {
        updated.conversation_termination.maximum_conversation_duration = current.max_conversation_time;
        changed = true;
      }
      if (updated.conversation_termination.termination_message == null) {
        updated.conversation_termination.termination_message = '';
        changed = true;
      }
    }

    // Conversation Turn Timeout
    if (!updated.conversation_turn_timeout) {
      updated.conversation_turn_timeout = {
        turn_timeout_seconds: current?.turn_timeout ?? '',
        turn_timeout_message: '',
        max_tries_for_conversation_turn_timeout: null
      };
      changed = true;
    } else {
      if (
        updated.conversation_turn_timeout.turn_timeout_seconds == null &&
        current?.turn_timeout != null
      ) {
        updated.conversation_turn_timeout.turn_timeout_seconds = current.turn_timeout;
        changed = true;
      }
      if (updated.conversation_turn_timeout.turn_timeout_message == null) {
        updated.conversation_turn_timeout.turn_timeout_message = '';
        changed = true;
      }
      if (updated.conversation_turn_timeout.max_tries_for_conversation_turn_timeout === '') {
        updated.conversation_turn_timeout.max_tries_for_conversation_turn_timeout = null;
        changed = true;
      }
    }

    // Interruptions
    if (!updated.interruptions) {
      updated.interruptions = {
        minimum_number_of_words: '',
        interruption_allowed_for_agent_first_message: ''
      };
      changed = true;
    } else {
      if (updated.interruptions.minimum_number_of_words == null) {
        updated.interruptions.minimum_number_of_words = '';
        changed = true;
      }
      if (updated.interruptions.interruption_allowed_for_agent_first_message == null) {
        updated.interruptions.interruption_allowed_for_agent_first_message = '';
        changed = true;
      }
    }

    // Background Effects
    if (!updated.background_effects) {
      updated.background_effects = {
        noise: ''
      };
      changed = true;
    } else {
      if (updated.background_effects.noise == null) {
        updated.background_effects.noise = '';
        changed = true;
      }
    }

    if (changed) setSelectedAgent(updated);
  }, [selectedAgent]);

  const handleEditAgent = (agent) => {
    // URL-based navigation to Agent Details page
    try {
      if (!client_id) return;
      navigateWithCurrentQuery(navigate, location, `/voice-agents/${client_id}/${selectedOptions}/agents/${agent.agent_id}`);
    } catch (e) {
      console.log(e);
    }
  };

  const getAllowedConfig = async () => {
    try {
      let res = await server.get_voice_agent_allowed_config()
      if(res.status === 200){
        let resp = await res.json()
        
        const newConfig = resp.data || {}
        setConfigData(newConfig)
        
        // Set LLM providers and initial model options
        const llmProviders = Object.keys(newConfig.llm || {})
        setLlmProviderOptions(llmProviders)
        
        // Set Voice providers and initial model options
        const voiceProviders = Object.keys(newConfig.voice || {})
        setVoiceProviderOptions(voiceProviders)
        
        // Set Transcription providers and initial model options
        const transcriptionProviders = Object.keys(newConfig.transcription || {})
        setTranscriptionProviderOptions(transcriptionProviders)

        // Set Languages options (supports multiple possible keys)
        const languages = Array.isArray(newConfig.languages)
          ? newConfig.languages
          : Array.isArray(newConfig.language)
            ? newConfig.language
            : Array.isArray(newConfig?.voice?.languages)
              ? newConfig.voice.languages
              : []
        setLanguageOptions(languages)
        const secondaryLangs = Array.isArray(newConfig.secondary_languages)
          ? newConfig.secondary_languages
          : languages
        setSecondaryLanguageOptions(secondaryLangs)
        
        // If agent has existing selections, update dropdowns accordingly
        if (selectedAgent) {
          updateModelOptions(selectedAgent.llm?.provider, 'llm', newConfig)
          updateVoiceModelOptions(selectedAgent.voice?.provider, newConfig)
          updateVoiceOptions(selectedAgent.voice?.provider, selectedAgent.voice?.model, selectedVoiceGender, newConfig)
          updateTranscriptionModelOptions(selectedAgent.transcription?.provider, newConfig)
        } else if (llmProviders.length > 0) {
          // Set default initial values
          updateModelOptions(llmProviders[0], 'llm', newConfig)
        }
        
        if (voiceProviders.length > 0) {
          updateVoiceModelOptions(voiceProviders[0], newConfig)
        }
        
        if (transcriptionProviders.length > 0) {
          updateTranscriptionModelOptions(transcriptionProviders[0], newConfig)
        }
      }
    } catch (error) {
      console.error("Error fetching allowed config:", error)
    }
  }

  // Keep model/voice/transcription option lists in sync when agent or config changes
  useEffect(() => {
    if (!selectedAgent) return;
    // LLM
    if (selectedAgent.llm?.provider) {
      updateModelOptions(selectedAgent.llm.provider, 'llm');
    } else {
      setLlmModelOptions([]);
    }
    // Voice
    if (selectedAgent.voice?.provider) {
      updateVoiceModelOptions(selectedAgent.voice.provider);
      if (selectedAgent.voice?.model) {
        const gender = selectedAgent.voice?.gender || selectedVoiceGender;
        updateVoiceOptions(selectedAgent.voice.provider, selectedAgent.voice.model, gender);
      } else {
        setVoiceOptions([]);
      }
    } else {
      setVoiceModelOptions([]);
      setVoiceOptions([]);
    }
    // Transcription
    if (selectedAgent.transcription?.provider) {
      updateTranscriptionModelOptions(selectedAgent.transcription.provider);
    } else {
      setTranscriptionModelOptions([]);
    }
  }, [configData, selectedAgent, selectedVoiceGender])

  // Auto-select sensible defaults when fields are empty and config is available
  useEffect(() => {
    if (!selectedAgent || !configData) return;
    let changed = false;
    const updated = { ...selectedAgent };

    // LLM defaults
    const llmProviders = Object.keys(configData.llm || {});
    const currentLlmProvider = updated?.llm?.provider;
    if (!currentLlmProvider || !configData?.llm?.[currentLlmProvider]) {
      if (llmProviders.length > 0) {
        updated.llm = { ...(updated.llm || {}), provider: llmProviders[0] };
        changed = true;
      }
    }
    const resolvedLlmProvider = updated?.llm?.provider;
    if (resolvedLlmProvider) {
      const llmModels = Object.keys(configData?.llm?.[resolvedLlmProvider] || {});
      const currentLlmModel = updated?.llm?.model;
      if (!currentLlmModel || !configData?.llm?.[resolvedLlmProvider]?.[currentLlmModel]) {
        if (llmModels.length > 0) {
          updated.llm = { ...(updated.llm || {}), model: llmModels[0] };
          changed = true;
        }
      }
    }

    // Voice defaults
    const voiceProviders = Object.keys(configData.voice || {});
    const currentVoiceProvider = updated?.voice?.provider;
    if (!currentVoiceProvider || !configData?.voice?.[currentVoiceProvider]) {
      if (voiceProviders.length > 0) {
        updated.voice = { ...(updated.voice || {}), provider: voiceProviders[0] };
        changed = true;
      }
    }
    const resolvedVoiceProvider = updated?.voice?.provider;
    if (resolvedVoiceProvider) {
      const voiceModels = Object.keys(configData?.voice?.[resolvedVoiceProvider] || {});
      const currentVoiceModel = updated?.voice?.model;
      if (!currentVoiceModel || !configData?.voice?.[resolvedVoiceProvider]?.[currentVoiceModel]) {
        if (voiceModels.length > 0) {
          updated.voice = { ...(updated.voice || {}), model: voiceModels[0] };
          changed = true;
        }
      }
      const gender = updated?.voice?.gender || selectedVoiceGender || 'female';
      const voices = (resolvedVoiceProvider && updated?.voice?.model)
        ? (configData?.voice?.[resolvedVoiceProvider]?.[updated.voice.model]?.voices?.[gender] || [])
        : [];
      const currentVoice = updated?.voice?.voice;
      
      // Check if current voice ID exists in the voices array
      const voiceExists = voices.some(voice => 
        typeof voice === 'object' ? voice.id === currentVoice : voice === currentVoice
      );
      
      if ((!currentVoice || !voiceExists) && voices.length > 0) {
        const firstVoiceId = getFirstVoiceId(resolvedVoiceProvider, updated.voice.model, gender);
        updated.voice = { ...(updated.voice || {}), voice: firstVoiceId, gender };
        changed = true;
      }

      // Handle voice speed defaults/clamping based on allowed config
      const speedCfg = configData?.voice?.[resolvedVoiceProvider]?.[updated.voice.model]?.speed;
      if (speedCfg?.available === 1) {
        const min = typeof speedCfg.min_value === 'number' ? speedCfg.min_value : 0.5;
        const max = typeof speedCfg.max_value === 'number' ? speedCfg.max_value : 2;
        const def = typeof speedCfg.default_value === 'number' ? speedCfg.default_value : 1;
        let currentSpeed = updated?.voice?.speed;
        if (typeof currentSpeed !== 'number') currentSpeed = def;
        if (currentSpeed < min) currentSpeed = min;
        if (currentSpeed > max) currentSpeed = max;
        if (updated?.voice?.speed !== currentSpeed) {
          updated.voice = { ...(updated.voice || {}), speed: currentSpeed };
          changed = true;
        }
      } else {
        if (updated?.voice?.speed != null) {
          const { speed, ...rest } = updated.voice || {};
          updated.voice = rest;
          changed = true;
        }
      }
    }

    // Transcription defaults
    const tProviders = Object.keys(configData.transcription || {});
    const currentTProvider = updated?.transcription?.provider;
    if (!currentTProvider || !configData?.transcription?.[currentTProvider]) {
      if (tProviders.length > 0) {
        updated.transcription = { ...(updated.transcription || {}), provider: tProviders[0] };
        changed = true;
      }
    }
    const resolvedTProvider = updated?.transcription?.provider;
    if (resolvedTProvider) {
      const tModels = Object.keys(configData?.transcription?.[resolvedTProvider] || {});
      const currentTModel = updated?.transcription?.model;
      if (!currentTModel || !configData?.transcription?.[resolvedTProvider]?.[currentTModel]) {
        if (tModels.length > 0) {
          updated.transcription = { ...(updated.transcription || {}), model: tModels[0] };
          changed = true;
        }
      }
    }

    if (changed) setSelectedAgent(updated);
  }, [configData, selectedAgent, selectedVoiceGender])

  // Ensure default language when options are available
  useEffect(() => {
    if (!selectedAgent) return;
    if (!selectedAgent.language && languageOptions && languageOptions.length > 0) {
      setSelectedAgent({ ...selectedAgent, language: languageOptions[0] });
    }
  }, [selectedAgent, languageOptions]);

  // Ensure default background noise when options are available
  useEffect(() => {
    if (!selectedAgent) return;
    const noiseOptions = (configData?.background_effects?.noise) || [];
    const currentNoise = selectedAgent?.background_effects?.noise;
    if ((currentNoise === '' || currentNoise == null) && noiseOptions.length > 0) {
      setSelectedAgent({
        ...selectedAgent,
        background_effects: {
          ...(selectedAgent?.background_effects || {}),
          noise: noiseOptions[0]
        }
      });
    }
  }, [selectedAgent, configData]);
  
  // Update model options based on selected provider
  const updateModelOptions = (provider, type, cfg) => {
    const effectiveConfig = cfg || configData;
    if (!provider || !effectiveConfig?.[type] || !effectiveConfig?.[type]?.[provider]) {
      setLlmModelOptions([])
      return
    }
    const models = Object.keys(effectiveConfig[type][provider] || {})
    setLlmModelOptions(models)
  }
  
  // Update voice model options based on selected voice provider
  const updateVoiceModelOptions = (provider, cfg) => {
    const effectiveConfig = cfg || configData;
    if (!provider || !effectiveConfig?.voice || !effectiveConfig?.voice?.[provider]) {
      setVoiceModelOptions([])
      return
    }
    const models = Object.keys(effectiveConfig.voice[provider] || {})
    setVoiceModelOptions(models)
    // Update voice options for the first model if available
    if (models.length > 0) {
      updateVoiceOptions(provider, models[0], selectedVoiceGender, effectiveConfig)
    }
  }
  
  // Helper function to get voice ID from voice name
  const getVoiceIdFromName = (provider, model, gender, voiceName) => {
    if (!provider || !model || !gender || !voiceName) return voiceName;
    
    const voices = configData?.voice?.[provider]?.[model]?.voices?.[gender] || [];
    const voiceObj = voices.find(voice => 
      typeof voice === 'object' && voice.id === voiceName
    );
    
    return voiceObj ? voiceObj.id : voiceName;
  };

  // Helper function to get voice name from voice ID
  const getVoiceNameFromId = (provider, model, gender, voiceId) => {
    if (!provider || !model || !gender || !voiceId) return voiceId;
    
    const voices = configData?.voice?.[provider]?.[model]?.voices?.[gender] || [];
    const voiceObj = voices.find(voice => 
      typeof voice === 'object' && voice.id === voiceId
    );
    
    return voiceObj ? voiceObj.id : voiceId;
  };

  // Helper function to get the first voice ID for a given provider/model/gender
  const getFirstVoiceId = (provider, model, gender, cfg) => {
    const effectiveConfig = cfg || configData;
    const voices = effectiveConfig?.voice?.[provider]?.[model]?.voices?.[gender] || [];
    if (voices.length === 0) return '';
    
    const firstVoice = voices[0];
    return typeof firstVoice === 'object' && firstVoice.id ? firstVoice.id : firstVoice;
  };

  // Update voice options based on selected provider, model and gender
  const updateVoiceOptions = (provider, model, gender, cfg) => {
    const effectiveConfig = cfg || configData;
    if (!provider || !model || !gender || 
        !effectiveConfig?.voice || 
        !effectiveConfig?.voice?.[provider] || 
        !effectiveConfig?.voice?.[provider]?.[model] || 
        !effectiveConfig?.voice?.[provider]?.[model]?.voices || 
        !effectiveConfig?.voice?.[provider]?.[model]?.voices?.[gender]) {
      setVoiceOptions([])
      return
    }
    const voices = effectiveConfig.voice[provider][model].voices[gender] || []
    // Convert voice objects to name strings for dropdown display
    const voiceNames = voices.map(voice => 
      typeof voice === 'object' && voice.id ? voice.id : voice
    )
    setVoiceOptions(voiceNames)
  }
   
  // Update transcription model options based on selected provider
  const updateTranscriptionModelOptions = (provider, cfg) => {
    const effectiveConfig = cfg || configData;
    if (!provider || !effectiveConfig?.transcription || !effectiveConfig?.transcription?.[provider]) {
      setTranscriptionModelOptions([])
      return
    }
    const models = Object.keys(effectiveConfig.transcription[provider] || {})
    setTranscriptionModelOptions(models)
  }

  const getAgentDetails = async (agent_id) => {
    try {
      setLoading(true)
      let res = await server.getAgentDetails(client_id, '', email, 'voice-agents', 'agents', agent_id)
      if(res.status === 200){
        let resp = await res.json()
        setSelectedAgent(resp.data)
        setLoading(false)
      }
    } catch (error) {
      setLoading(false)
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  // Show the modal to get agent name first
  const handleCreateAgent = () => {
    setNewAgentName('');
    setNewAgentError('');
    setShowCreateAgentModal(true);
  };

  const handleTestWorkflowChange = (e) => {
    const choice = e.target.value;
    if (choice === 'Test in Browser') {
      startCall();
    } else if (choice === 'Test Outbound') {
      setShowOutboundModal(true);
    }
    setTestWorkflowOption('');
  };

  const handleSubmitOutbound = async () => {
    try {
      if(!outboundPhoneNumber){
        showToast('Phone number is required', 'error');
        return;
      }
      let res = await server.test_voice_agent_outbound_call(client_id, selectedAgent.agent_id, outboundPhoneNumber, selectedAgent.phonenumber, email, 'voice-agents', undefined);
      if(res.status === 200){
        let resp = await res.json();
        showToast(resp.message, 'success');
      }
    } catch (error) {
      showToast("Failed to initiate outbound call", 'error');
    }
    setShowOutboundModal(false);
    setOutboundPhoneNumber('');
  };
  
  // Generate a random agent ID starting with VA
  const generateAgentId = () => {
    const randomString = Math.random().toString(36).substring(2, 10).toUpperCase();
    return `VA${randomString}`;
  };
  
  // Process agent creation after getting name
  const handleSubmitAgentName = () => {
    // Validate agent name
    if (!newAgentName.trim()) {
      setNewAgentError('Agent name is required');
      return;
    }
    // Close the modal and navigate to URL-based create flow
    setShowCreateAgentModal(false);
    const newId = generateAgentId();
    if (!client_id) return;
    const encodedName = encodeURIComponent(newAgentName.trim());
    navigate(`/voice-agents/${client_id}/${selectedOptions}/agents/${newId}?action=create&name=${encodedName}`);
  };

  const openAgentActionModal = (agent, action) => {
    setAgentActionModal({
      open: true,
      action,
      agent,
    });
  };

  const getAgentActionCopy = (action) => {
    if (action === 'duplicate') {
      return {
        title: 'Duplicate Agent',
        verb: 'duplicate',
        button: 'Duplicate',
        loading: 'Duplicating...',
        success: 'duplicated',
        icon: 'duplicate',
      };
    }
    if (action === 'unarchive') {
      return {
        title: 'Unarchive Agent',
        verb: 'unarchive',
        button: 'Unarchive',
        loading: 'Unarchiving...',
        success: 'unarchived',
        icon: 'restore',
      };
    }
    return {
      title: 'Archive Agent',
      verb: 'archive',
      button: 'Archive',
      loading: 'Archiving...',
      success: 'archived',
      icon: 'archive',
    };
  };

  const closeAgentActionModal = (force = false) => {
    if (agentActionLoading && !force) return;
    setAgentActionModal({
      open: false,
      action: '',
      agent: null,
    });
  };

  const handleConfirmAgentAction = async () => {
    const { action, agent } = agentActionModal;
    if (!action || !agent) return;

    try {
      setAgentActionLoading(true);
      const res = await server.saveAgentDetails({
        content: agent,
        agent_id: agent.agent_id || null,
        agent_name: agent.agent_name,
        phonenumber: agent.phonenumber,
        client_id: client_id,
        service_id: '',
        email: email,
        menu: 'voice-agents',
        submenu: 'agents',
        action,
      });

      const resp = await res.json().catch(() => ({}));

      if (res.status === 200) {
        const actionCopy = getAgentActionCopy(action);
        showToast(resp.message || `Agent ${actionCopy.success} successfully`, 'success');
        closeAgentActionModal(true);
        getAgentsList();
      } else {
        showToast(resp.error || resp.message || `Failed to ${action} agent`, 'error');
      }
    } catch (error) {
      console.log(error);
      showToast(`Failed to ${action} agent`, 'error');
    } finally {
      setAgentActionLoading(false);
    }
  };

  const handleSaveAgent = async () => {
    try {
      setLoading(true);
      let obj = {
        content: selectedAgent,
        agent_id: selectedAgent.agent_id || null,
        agent_name: selectedAgent?.agent_name,
        phonenumber: selectedAgent?.phonenumber,
        client_id: client_id,
        service_id: '',
        email: email,
        menu: 'voice-agents',
        submenu: 'agents',
      }
      let res = await server.saveAgentDetails(obj)
      if(res.status === 200){
        let resp = await res.json()
        console.log(resp)
        setView('list');
        setSelectedAgent(null);
        setIsCreating(false);
        getAgentsList()
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false)
    }
  };

  const endCall = () => {
    // Stop recording and reset timer
    if (isRecording) {
      setIsRecording(false);
      setRecordingDuration(0);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    disconnectLiveKitClient();
    
    // Also clear the audio element
    if (audioElementRef.current) {
      audioElementRef.current.srcObject = null;
    }
    
    setIsCallActive(false);
    setCallStatus('');
    setIsAudioPlaying(false);
    setAudioQuality('Unknown');
  };

  const disconnectLiveKitClient = () => {
    const client = livekitClientRef.current;
    if (!client) return;
    livekitClientRef.current = null;
    client.disconnect().catch(() => {});
  };

  const handlePhoneNumberChange = (newPhoneNumber) => {
    // If there's an active call, end it first
    if (isCallActive) {
      endCall();
    }
    // Update the phone number
    setSelectedAgent({...selectedAgent, phonenumber: newPhoneNumber});
  };

  const waitForIceGatheringComplete = async (pc, timeoutMs = 2000) => {
    if (pc.iceGatheringState === 'complete') return;
    return new Promise((resolve) => {
        let timeoutId;
        const done = () => { pc.removeEventListener('icegatheringstatechange', check); clearTimeout(timeoutId); resolve(); };
        const check = () => { if (pc.iceGatheringState === 'complete') done(); };
        pc.addEventListener('icegatheringstatechange', check);
        timeoutId = setTimeout(done, timeoutMs);
        check();
    });
};

  // Fixed WebRTC audio setup in your frontend (AgentsPage.js)

const startCall = async () => {
  try {
    // Request audio with specific constraints for better recording
    const stream = await navigator.mediaDevices.getUserMedia({ 
      audio: {
        channelCount: 1,
        sampleRate: 16000,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        // Add these for better audio capture
        latency: 0.01,
        volume: 1.0
      }
    })
    
    // Store the stream in the ref for proper cleanup
    localStreamRef.current = stream;

    // Start the session first: the response decides the transport (LiveKit or SmallWebRTC).
    const res = await server.browserTesting(client_id, selectedAgent.agent_id, email, 'voice-agents', undefined);
    if (res.status !== 200) {
      try {
        const err = await res.json().catch(() => null);
        const message = err?.error || 'Failed to start browser call';
        showToast(message, 'error');
      } catch (_) {
        showToast('Failed to start browser call', 'error');
      }
      setIsCallActive(false);
      setCallStatus('Error in startCall');
      disconnect();
      return;
    }
    const resp = await res.json();
    const session = resp.data;

    if (isLiveKitBrowserCall(session)) {
      // The Pipecat client captures the mic itself; the stream above only secured permission.
      stream.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
      livekitClientRef.current = await startLiveKitBrowserCall({
        session,
        audioElement: audioElementRef.current,
        onEnded: () => disconnect(),
      });
      setIsRecording(true);
      setRecordingDuration(0);
      setIsCallActive(true);
      setCallStatus('Connected to voice agent');
      return;
    }

    const pc = new RTCPeerConnection({ 
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] 
    })
    
    // Store the peer connection in the ref for proper cleanup
    peerConnectionRef.current = pc;
    
    // FIXED: Use sendrecv for bidirectional audio
    const audioTrack = stream.getAudioTracks()[0];
    
    // Add local audio track for sending
    pc.addTrack(audioTrack, stream);
    
    // CRITICAL FIX: Change direction to 'sendrecv' for bidirectional audio
    pc.addTransceiver('audio', { direction: 'sendrecv' });
    pc.addTransceiver('video', { direction: 'recvonly' }); // Video still receive-only
    
    // Handle incoming audio stream for playback
    pc.ontrack = e => {
      const el = audioElementRef.current;
      if (!el) return;
    
      const [stream] = e.streams;
      if (!stream) return;
    
      // Avoid resetting if unchanged
      if (el.srcObject === stream) return;
    
      try { el.pause(); } catch (_) {}
    
      el.muted = false;
      el.playsInline = true;
      el.srcObject = stream;
    
      const tryPlay = () => {
        const p = el.play();
        if (p && typeof p.then === 'function') {
          p.catch(err => {
            if (err?.name === 'AbortError') {
              setTimeout(() => el.play().catch(() => {}), 0);
            }
          });
        }
      };
    
      if (el.readyState >= 2) {
        tryPlay();
      } else {
        el.oncanplay = () => {
          el.oncanplay = null;
          tryPlay();
        };
      }
    };

    // Auto handle backend disconnects and failures
    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      console.log('WebRTC connection state:', state);
      if (state === 'disconnected' || state === 'failed' || state === 'closed') {
        disconnect();
      }
    };
    
    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState;
      console.log('ICE connection state:', state);
      if (state === 'disconnected' || state === 'failed' || state === 'closed') {
        disconnect();
      }
    };

    // Create offer with proper constraints
    await pc.setLocalDescription(await pc.createOffer({
      offerToReceiveAudio: true, 
      offerToReceiveVideo: false // We don't need video
    }));
    
    await waitForIceGatheringComplete(pc);

    // Send offer to orchestrator
    const res2 = await fetch(session.connect_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sdp: pc.localDescription.sdp,
          type: pc.localDescription.type
        })
    });
    
    const answer = await res2.json();
    await pc.setRemoteDescription(answer);

    setIsRecording(true);
    setRecordingDuration(0);
    setIsCallActive(true);
    setCallStatus('Connected to voice agent');

    console.log('WebRTC connection established successfully');
  } catch (error) {
    console.error('Error in startCall:', error);
    setIsCallActive(false);
    setCallStatus('Error in startCall');
    disconnect();
  }
};
  const disconnect = () => {
    try {
      // Stop recording and get recording info
      if (isRecording) {
        setIsRecording(false);
        setRecordingDuration(0);
        
        // Create recording info (you'll need to implement actual recording logic)
        const recordingInfo = {
          id: `recording_${Date.now()}`,
          duration: recordingDuration * 1000, // Convert to milliseconds
          size: 0, // You'll need to implement actual size calculation
          startTime: Date.now() - (recordingDuration * 1000),
          endTime: Date.now(),
          format: 'audio/webm'
        };
        
        setRecordingInfo(recordingInfo);
        
        // Add to recordings list
        if (recordingDuration > 0) {
          setRecordings(prev => [...prev, {
            ...recordingInfo,
            timestamp: new Date().toISOString(),
            id: recordingInfo.id
          }]);
        }
      }
      
      // Stop all local media tracks
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
        localStreamRef.current = null;
      }
      
      // Close and cleanup peer connection
      if (peerConnectionRef.current) {
        try { 
          peerConnectionRef.current.getSenders().forEach(s => s.track && s.track.stop()) 
        } catch (e) {
          console.log('Error stopping sender tracks:', e);
        }
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
      disconnectLiveKitClient();
      
      // Clear audio element
      if (audioElementRef.current) {
        audioElementRef.current.srcObject = null;
      }
      
      // Reset all call states
      setIsCallActive(false);
      setCallStatus('');
      setIsAudioPlaying(false);
      setAudioQuality('Unknown');
      
      console.log('Call disconnected successfully');
    } catch (error) {
      console.log('Error in disconnect:', error);
    }
  };

  const getPhoneNumbersList = async () => {
    try {
      let res = await server.get_voice_agent_phone_number_list(client_id, '', email, 'voice-agents', 'agents', 1)
      if(res.status === 200){
        let resp = await res.json()
        let numbers = []
        for(let i=0;i<resp.phone_numbers.length;i++){
            numbers.push(resp.phone_numbers[i].phone_number)
        }
        setPhoneNumbers(numbers)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const getAgentsList = async () => {
    try {
      setLoading(true);
      let res = await server.getAgentsList(client_id, '', email, 'voice-agents', 'agents')
      if(res.status === 200){
        let resp = await res.json()
        setAgents(resp.data)
        setLoading(false)
      }
    } catch (error) {
      setLoading(false)
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if(client_id && email){
      getAgentsList()
    }
  }, [client_id, email]);

  // Cleanup WebRTC resources on component unmount
  useEffect(() => {
    return () => {
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }
      if (livekitClientRef.current) {
        livekitClientRef.current.disconnect().catch(() => {});
        livekitClientRef.current = null;
      }
      if (audioElementRef.current) {
        audioElementRef.current.srcObject = null;
      }
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    };
  }, []);

  // Open create form if navigation state or query param requests it
  useEffect(() => {
    const stateFlag = location?.state?.openCreate;
    const params = new URLSearchParams(location.search);
    const action = params.get('action');
    const shouldOpen = stateFlag || action === 'create';
    if (shouldOpen && !showCreateAgentModal) {
      // Show the agent creation modal instead of directly going to configure view
      setShowCreateAgentModal(true);
      setNewAgentName('');
      setNewAgentError('');

      // Clean param/state so refresh doesn't keep reopening
      const nextParams = new URLSearchParams(location.search);
      if (nextParams.get('action') === 'create') {
        nextParams.delete('action');
      }
      navigate(
        `${location.pathname}${nextParams.toString() ? `?${nextParams.toString()}` : ''}`,
        { replace: true, state: { ...(location.state || {}), openCreate: false } }
      );
    }
  }, [location.search, location.state, navigate, showCreateAgentModal, location.pathname]);

  const convertEpochToLocalTime = (epoch) => {
    return new Date(epoch * 1000).toLocaleString();
  }
  // Add recording duration timer
useEffect(() => {
  if (isRecording) {
    recordingIntervalRef.current = setInterval(() => {
      setRecordingDuration(prev => prev + 1);
    }, 1000);
  } else {
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
  }

  return () => {
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }
  };
}, [isRecording]);

  useEffect(()=> {
    if(clientID){
      setClient_id(clientID)
    }
    const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
    if (user) {
      setEmail(user.email);
    }
  }, [clientID])

  function getCookie(name) {
      function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
      var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
      return match ? match[1] : null;
  }

  const isAgentArchived = (agent) => (
    agent?.status === 'archived' ||
    agent?.archived === true ||
    agent?.is_archived === true
  );
  const normalizedAgentSearchQuery = agentSearchQuery.trim().toLowerCase();
  const filteredAgents = agents.filter((agent) => {
    if (isAgentArchived(agent) !== showArchivedAgents) {
      return false;
    }

    if (!normalizedAgentSearchQuery) {
      return true;
    }

    return [agent?.agent_name, agent?.agent_id].some((value) =>
      String(value ?? '').toLowerCase().includes(normalizedAgentSearchQuery)
    );
  });
  const currentAgentActionCopy = getAgentActionCopy(agentActionModal.action);

  return (
    <div className="py-3 px-5 font-sans bg-gray-50 h-full">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-semibold text-gray-900">Agents</h1>
        <button
          onClick={handleCreateAgent}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded text-sm"
        >
          <Plus className="w-4 h-4 inline mr-1" /> Create Agent
        </button>
      </div>

      {!loading && <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-4 py-3 border-b flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:flex-1">
            <label htmlFor="agent-search" className="sr-only">Search agents by name or ID</label>
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
            <input
              id="agent-search"
              type="search"
              value={agentSearchQuery}
              onChange={(e) => setAgentSearchQuery(e.target.value)}
              placeholder="Search by name or agent ID"
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <label className="flex items-center text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showArchivedAgents}
              onChange={(e) => setShowArchivedAgents(e.target.checked)}
              className="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            Show Archived Agents
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated At</th>
                
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredAgents.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                    {agentSearchQuery.trim()
                      ? 'No agents match your search.'
                      : showArchivedAgents
                        ? 'No archived agents found.'
                        : 'No agents found.'}
                  </td>
                </tr>
              )}
              {filteredAgents.map((agent) => (
                <tr key={agent.agent_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">{agent.agent_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{agent.agent_id}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{agent.phonenumber}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{agent.created_by}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{convertEpochToLocalTime(agent.updated_at)}</td>
                  
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {isAgentArchived(agent) ? (
                        <button
                          onClick={() => openAgentActionModal(agent, 'unarchive')}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-green-700 hover:bg-green-50"
                          title="Unarchive agent"
                          aria-label={`Unarchive ${agent.agent_name || 'agent'}`}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditAgent(agent)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-indigo-600 hover:bg-indigo-50"
                            title="Edit agent"
                            aria-label={`Edit ${agent.agent_name || 'agent'}`}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openAgentActionModal(agent, 'duplicate')}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                            title="Duplicate agent"
                            aria-label={`Duplicate ${agent.agent_name || 'agent'}`}
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openAgentActionModal(agent, 'archive')}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-amber-700 hover:bg-amber-50"
                            title="Archive agent"
                            aria-label={`Archive ${agent.agent_name || 'agent'}`}
                          >
                            <Archive className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>}
      {
        loading && (
          <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
            <div className="loader"></div>
            <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
          </div>
        )
      }
      {/* Create Agent Modal */}
      {showCreateAgentModal && (
        <div style={{position: "fixed", left: "0%", top: "0%", width: "100%", height: "100%", background: "rgba(0, 0, 0, 0.5)", zIndex: "1001"}}>
          <div style={{
            position: "fixed", 
            left: "50%", 
            top: "50%", 
            transform: "translate(-50%, -50%)",
            width: "450px", 
            background: "white", 
            borderRadius: "10px", 
            boxShadow: "rgba(0, 0, 0, 0.2) 0px 4px 12px", 
            zIndex: "999999999", 
            padding: "20px"
          }}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                  <User size={18} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Create New Agent</h3>
              </div>
              <div 
                style={{cursor: "pointer"}} 
                onClick={() => setShowCreateAgentModal(false)}
              >
                <FaTimes style={{color: "slategray"}} />
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                Let's start by giving your voice agent a name. You'll be able to configure all other details in the next step.
              </p>
              
              <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                placeholder="Enter agent name"
                value={newAgentName}
                onChange={(e) => setNewAgentName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmitAgentName();
                  }
                }}
                className={`w-full px-3 py-2 border ${newAgentError ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                autoFocus
              />
              {newAgentError && (
                <p className="mt-1 text-sm text-red-500">{newAgentError}</p>
              )}
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowCreateAgentModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitAgentName}
                className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate / Archive Confirmation Modal */}
      {agentActionModal.open && (
        <div style={{position: "fixed", left: "0%", top: "0%", width: "100%", height: "100%", background: "rgba(0, 0, 0, 0.5)", zIndex: "1001"}}>
          <div style={{
            position: "fixed",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: "430px",
            maxWidth: "calc(100% - 32px)",
            background: "white",
            borderRadius: "10px",
            boxShadow: "rgba(0, 0, 0, 0.2) 0px 4px 12px",
            zIndex: "999999999",
            padding: "20px"
          }}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center">
                  {currentAgentActionCopy.icon === 'duplicate' ? (
                    <Copy size={18} />
                  ) : currentAgentActionCopy.icon === 'restore' ? (
                    <RotateCcw size={18} />
                  ) : (
                    <Archive size={18} />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {currentAgentActionCopy.title}
                </h3>
              </div>
              <button
                type="button"
                style={{cursor: agentActionLoading ? "not-allowed" : "pointer"}}
                onClick={() => closeAgentActionModal()}
                disabled={agentActionLoading}
                aria-label="Close confirmation modal"
              >
                <FaTimes style={{color: "slategray"}} />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600">
                Are you sure you want to {currentAgentActionCopy.verb}{" "}
                <span className="font-medium text-gray-900">
                  {agentActionModal.agent?.agent_name || 'this agent'}
                </span>
                ?
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => closeAgentActionModal()}
                disabled={agentActionLoading}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAgentAction}
                disabled={agentActionLoading}
                className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {agentActionLoading ? currentAgentActionCopy.loading : currentAgentActionCopy.button}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentsPage;
